import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'registrationType',
    'orgFields',
    'userPhone',
    'userEmail',
    'orgPhone',
    'orgPhoneSame',
    'orgEmail',
    'orgEmailSame',
    'heardAboutUsSource',
    'heardAboutUsDetailGroup',
    'heardAboutUsDetailLabel',
    'heardAboutUsDetailInput',
    'industryNameSource',
    'industryNameDetailGroup',
    'industryNameDetailInput',
    'step',
    'stepLabel',
    'progressBars',
    'navigation',
    'submitButton',
  ]

  static values = { errorFields: Array }

  connect() {
    this.currentStep = 1
    this.updateRegistrationVisibility()
    this.syncOrgPhone()
    this.syncOrgEmail()
    this.updateCompanyOnlyVisibility()
    this.syncHeardAboutUsDetail()
    this.syncIndustryNameDetail()
    this.updateAllPasswordIcons()
    this.showFirstStepWithError()
  }

  toggleRegistrationType() {
    this.currentStep = 1
    this.renderRegistrationStep()
  }

  nextStep() {
    if (!this.hasStepTarget || !this.currentStepIsValid()) return

    this.currentStep += 1
    this.renderRegistrationStep({ focus: true })
  }

  previousStep() {
    if (!this.hasStepTarget || this.currentStep === 1) return

    this.currentStep -= 1
    this.renderRegistrationStep({ focus: true })
  }

  prepareSubmission(event) {
    if (!this.hasStepTarget) return

    const totalSteps = this.registrationType === 'company' ? 3 : 2
    if (this.currentStep !== totalSteps) {
      event.preventDefault()
      return
    }

    this.stepTargets.forEach((step) => {
      const isCompanyOnly = step.dataset.authFormStep === 'company:1'
      this.setStepControlsDisabled(
        step,
        this.registrationType !== 'company' && isCompanyOnly,
      )
    })
  }

  toggleOrgPhoneSame() {
    this.syncOrgPhone()
  }

  toggleOrgEmailSame() {
    this.syncOrgEmail()
  }

  syncUserPhone() {
    if (this.hasOrgPhoneSameTarget && this.orgPhoneSameTarget.checked) {
      this.syncOrgPhone()
    }
  }

  syncUserEmail() {
    if (this.hasOrgEmailSameTarget && this.orgEmailSameTarget.checked) {
      this.syncOrgEmail()
    }
  }

  syncHeardAboutUsDetail() {
    if (
      !this.hasHeardAboutUsSourceTarget ||
      !this.hasHeardAboutUsDetailGroupTarget ||
      !this.hasHeardAboutUsDetailLabelTarget ||
      !this.hasHeardAboutUsDetailInputTarget
    )
      return

    const selectedSource = this.heardAboutUsSourceTarget.value
    const requiresReferralInput = selectedSource === 'referral'
    const requiresOtherInput = selectedSource === 'other'
    const requiresDetailInput = requiresReferralInput || requiresOtherInput

    this.heardAboutUsDetailGroupTarget.style.display = requiresDetailInput
      ? 'block'
      : 'none'
    this.heardAboutUsDetailInputTarget.required = requiresDetailInput

    if (requiresReferralInput) {
      this.heardAboutUsDetailLabelTarget.textContent = "Referrer's name"
      this.heardAboutUsDetailInputTarget.placeholder =
        'Enter the name of the person who referred you'
      return
    }

    if (requiresOtherInput) {
      this.heardAboutUsDetailLabelTarget.textContent = 'Please specify'
      this.heardAboutUsDetailInputTarget.placeholder =
        'Tell us where you heard about us'
      return
    }

    this.heardAboutUsDetailInputTarget.value = ''
  }

  syncIndustryNameDetail() {
    if (
      !this.hasIndustryNameSourceTarget ||
      !this.hasIndustryNameDetailGroupTarget ||
      !this.hasIndustryNameDetailInputTarget
    )
      return

    const requiresDetailInput = this.industryNameSourceTarget.value === 'other'
    this.industryNameDetailGroupTarget.style.display = requiresDetailInput
      ? 'block'
      : 'none'
    this.industryNameDetailInputTarget.required = requiresDetailInput

    if (!requiresDetailInput) {
      this.industryNameDetailInputTarget.value = ''
    }
  }

  togglePassword(event) {
    const toggleButton = event.currentTarget
    const targetId = toggleButton.dataset.passwordToggle
    const input = this.element.querySelector(`[id="${targetId}"]`)
    if (!input) return

    input.type = input.type === 'password' ? 'text' : 'password'
    this.renderPasswordIcon(toggleButton, input.type === 'password')
  }

  updateRegistrationVisibility() {
    if (!this.hasRegistrationTypeTarget || !this.hasOrgFieldsTarget) return
    this.orgFieldsTarget.hidden =
      this.registrationType !== 'company' || this.currentStep !== 1
    this.updateCompanyOnlyVisibility()
  }

  updateCompanyOnlyVisibility() {
    this.element
      .querySelectorAll('[data-auth-form-company-only]')
      .forEach((element) => {
        element.hidden = this.registrationType !== 'company'
      })
  }

  currentStepIsValid() {
    const currentSteps = this.stepTargets.filter((step) =>
      this.stepMatchesCurrentRegistration(step),
    )
    const requiredFields = currentSteps.flatMap((step) =>
      [...step.querySelectorAll('[required]')].filter(
        (field) => !field.disabled,
      ),
    )
    const firstInvalidField = requiredFields.find(
      (field) => !field.checkValidity(),
    )

    if (!firstInvalidField) return true

    firstInvalidField.reportValidity()
    firstInvalidField.focus()
    return false
  }

  showFirstStepWithError() {
    if (!this.hasStepTarget) return

    const errorField = this.errorFieldsValue
      .map((fieldName) =>
        this.element.querySelector(`[name="user[${fieldName}]"]`),
      )
      .find(Boolean)
    const step = errorField?.closest('[data-auth-form-target~="step"]')

    if (step) this.currentStep = this.stepNumberFor(step)
    this.renderRegistrationStep()
  }

  renderRegistrationStep({ focus = false } = {}) {
    if (!this.hasStepTarget || !this.hasRegistrationTypeTarget) return

    const totalSteps = this.registrationType === 'company' ? 3 : 2
    this.stepTargets.forEach((step) => {
      const isVisible = this.stepMatchesCurrentRegistration(step)
      step.hidden = !isVisible
      step.setAttribute('aria-hidden', (!isVisible).toString())
      this.setStepControlsDisabled(step, !isVisible)
    })

    this.updateRegistrationVisibility()
    this.renderProgress(totalSteps)

    if (this.hasNavigationTarget) {
      this.navigationTarget.querySelector('.registration-wizard__back').hidden =
        this.currentStep === 1
      this.navigationTarget.querySelector(
        '.registration-wizard__continue',
      ).hidden = this.currentStep === totalSteps
      this.submitButtonTarget.hidden = this.currentStep !== totalSteps
      this.submitButtonTarget.disabled = this.currentStep !== totalSteps
      this.submitButtonTarget.value =
        this.registrationType === 'company'
          ? 'Create company account'
          : 'Create account'
    }

    if (focus) requestAnimationFrame(() => this.stepLabelTarget.focus())
  }

  renderProgress(totalSteps) {
    this.stepLabelTarget.textContent = `Step ${this.currentStep} of ${totalSteps}`
    this.progressBarsTarget.replaceChildren(
      ...Array.from({ length: totalSteps }, (_, index) => {
        const bar = document.createElement('span')
        bar.className = 'registration-wizard__bar'
        if (index < this.currentStep) bar.classList.add('is-active')
        return bar
      }),
    )
  }

  stepMatchesCurrentRegistration(step) {
    const [registrationType, stepNumber] =
      step.dataset.authFormStep
        .split(' ')
        .map((value) => value.split(':'))
        .find(
          ([type, number]) =>
            type === this.registrationType &&
            Number(number) === this.currentStep,
        ) || []

    return (
      registrationType === this.registrationType &&
      Number(stepNumber) === this.currentStep
    )
  }

  stepNumberFor(step) {
    const match = step.dataset.authFormStep
      .split(' ')
      .map((value) => value.split(':'))
      .find(([type]) => type === this.registrationType)

    return Number(match?.[1]) || 1
  }

  setStepControlsDisabled(step, disabled) {
    step
      .querySelectorAll('input, select, textarea, button')
      .forEach((field) => {
        if (disabled) {
          if (field.dataset.authFormInitiallyDisabled === undefined) {
            field.dataset.authFormInitiallyDisabled = field.disabled.toString()
          }
          field.disabled = true
          return
        }

        if (field.dataset.authFormInitiallyDisabled !== undefined) {
          field.disabled = field.dataset.authFormInitiallyDisabled === 'true'
          delete field.dataset.authFormInitiallyDisabled
        }
      })
  }

  get registrationType() {
    return this.registrationTypeTarget.value
  }

  // Organisation fields live on company step 1, so the visible step 2 field is filled from them.
  syncOrgPhone() {
    const userPhone = this.element.querySelector('#user_phone')
    const orgPhone = this.element.querySelector('#org_phone')
    const copyPhone = this.element.querySelector('#org_phone_same')

    if (!userPhone || !orgPhone || !copyPhone) return

    if (copyPhone.checked) {
      userPhone.value = orgPhone.value
      userPhone.readOnly = true
    } else {
      userPhone.readOnly = false
    }
  }

  syncOrgEmail() {
    const userEmail = this.element.querySelector('#user_email')
    const orgEmail = this.element.querySelector('#org_email')
    const copyEmail = this.element.querySelector('#org_email_same')

    if (!userEmail || !orgEmail || !copyEmail) return

    if (copyEmail.checked) {
      userEmail.value = orgEmail.value
      userEmail.readOnly = true
    } else {
      userEmail.readOnly = false
    }
  }

  updateAllPasswordIcons() {
    this.element
      .querySelectorAll('[data-password-toggle]')
      .forEach((toggleButton) => {
        const targetId = toggleButton.dataset.passwordToggle
        const input = this.element.querySelector(`[id="${targetId}"]`)
        if (!input) return
        this.renderPasswordIcon(toggleButton, input.type === 'password')
      })
  }

  renderPasswordIcon(toggleButton, isHidden) {
    toggleButton.innerHTML = isHidden ? this.eyeOffIconSvg() : this.eyeIconSvg()
    toggleButton.setAttribute(
      'aria-label',
      isHidden ? 'Show password' : 'Hide password',
    )
    toggleButton.setAttribute(
      'title',
      isHidden ? 'Show password' : 'Hide password',
    )
  }

  eyeIconSvg() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8"></path><circle cx="12" cy="12" r="3"></circle></svg>'
  }

  eyeOffIconSvg() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8"></path><circle cx="12" cy="12" r="3"></circle><line x1="3" y1="3" x2="21" y2="21"></line></svg>'
  }
}
